import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import { validateFormat, validatePresence } from 'ember-changeset-validations/validators';
import { dropTask, type Task } from 'ember-concurrency';
import { runTask } from 'ember-lifeline';
import set from 'ember-set-helper/helpers/set';
import { or } from 'ember-truth-helpers';
import preventDefault from 'garaje/helpers/prevent-default';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type SessionsService from 'garaje/services/session';
import urlBuilder from 'garaje/utils/url-builder';
import type { SinglePayload } from 'jsonapi/response';
import LiquidIf from 'liquid-fire/components/liquid-if';

const Validations = {
  email: [
    validatePresence({
      presence: true,
      message: 'is a required field',
    }),
    validateFormat({
      type: 'email',
      allowBlank: false,
    }),
  ],
};

type SearchPayload = SinglePayload<{ usesCredentials: boolean; samlTo: string; confirmed: boolean }>;

interface EmailSearchComponentSignature {
  Args: {
    authenticateTask: Task<void, [string, string]>;
    companyId: number;
    email: string;
    emailFound: boolean;
    password: string;
    prefilledEmail: string;
    rd: string;
    samlUrl: string | null | undefined;
    changeEmail: (email: string | undefined) => void;
    handleSearchResult: (email: string, found: boolean, payload?: SearchPayload) => void;
    redirectToEnvoy: (path: string) => void;
    logInViaSAML: () => void;
  };
}

type EmailChangeset = DetailedChangeset<{ email?: string }>;

export default class EmailSearchComponent extends Component<EmailSearchComponentSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare session: SessionsService;

  @tracked password: string = '';
  @tracked email: string = '';
  @tracked changeset: EmailChangeset;

  constructor(owner: unknown, args: EmailSearchComponentSignature['Args']) {
    super(owner, args);

    const { password, prefilledEmail, email } = args;

    this.password = password || '';
    this.email = (prefilledEmail ?? email) || '';
    this.changeset = this.#buildChangeset();

    if (prefilledEmail) {
      // Use delayed task to break-up changeset.email assignments into separate computations to avoid error:
      // "You attempted to update `_errors` on `changeset`, but it had already been used previously in the same computation."
      runTask(
        this,
        () => {
          void this.handleSubmitTask.perform(prefilledEmail, '');
        },
        1,
      );
    }
  }

  get invalid(): boolean {
    return Boolean(this.changeset.error?.['email']);
  }

  #buildChangeset(): EmailChangeset {
    const validator = lookupValidator(Validations);
    return Changeset({ email: this.email }, validator, Validations);
  }

  onUpdate = (): void => {
    if (this.args.emailFound && this.args.prefilledEmail) {
      void this.args.authenticateTask.perform(this.args.prefilledEmail, this.password || '');
    }
  };

  validate = (): void => {
    if (this.email) {
      this.changeset.email = this.email;
    }
  };

  handleSubmitTask = dropTask(async (email: string | undefined, password: string | undefined) => {
    email = email || '';
    password = password || '';

    if (this.args.emailFound) {
      await this.args.authenticateTask.perform(email, password);
    } else {
      this.changeset.email = email;

      await this.changeset.validate();

      if (this.changeset.isValid) {
        await this.searchEmailTask.perform(this.email);
      }
    }
  });

  searchEmailTask = dropTask(async (email: string) => {
    const url = urlBuilder.v2.users.search();

    try {
      const data: Record<string, unknown> = {
        email,
        company_id: this.args.companyId,
      };
      if (this.args.rd === 'legacy-mobile') {
        data['legacy_mobile'] = true;
      }
      const payload = await this.ajaxFetch.request<SearchPayload>(url, { data });

      this.args.handleSearchResult(email, true, payload);
    } catch (_e) {
      this.args.handleSearchResult(email, false);
    }
  });

  <template>
    <form
      class="new_user"
      id="new_user"
      autocomplete="off"
      {{didUpdate this.onUpdate @emailFound}}
      {{on "submit" (preventDefault (fn this.handleSubmitTask.perform this.email this.password))}}
      data-test-email-search-form
    >
      <h3 class="text-center mb-8">Log in to the Envoy dashboard</h3>
      {{! LiquidIf component still uses @class, disable lint for the opening tag }}
      {{! template-lint-disable no-at-class }}
      <LiquidIf @predicate={{@emailFound}} @tagName="fieldset" @class="login-details m-0">
        {{! template-lint-enable no-at-class }}
        <:default>
          <div data-test-field="password">
            <label for="passwordField">
              <LiquidIf @predicate={{true}}>
                Password
              </LiquidIf>
            </label>
            <input
              id="passwordField"
              type="password"
              value={{this.password}}
              autocomplete="off"
              placeholder="Password"
              class="password-field"
              data-private
              data-canary="login-password-field"
              {{on "input" (pick "target.value" (set this "password"))}}
            />
          </div>
          <button
            type="submit"
            class="btn btn--medium btn--brand w-full"
            data-canary="login-button"
            data-test-login-button
          >
            <span class="lock"></span>
            Log in
          </button>
        </:default>
        <:else>
          <div data-test-field="email">
            <label data-test-field-label="email" for="emailField" class={{if this.invalid "error-description"}}>
              <LiquidIf @predicate={{this.invalid}}>
                <:default>
                  Please format email correctly
                </:default>
                <:else>
                  Company email address
                </:else>
              </LiquidIf>
            </label>
            <input
              id="emailField"
              type="email"
              value={{this.email}}
              data-private
              placeholder="email address"
              class={{if this.invalid "error"}}
              data-canary="login-email-field"
              {{on "input" (pick "target.value" (set this "email"))}}
              {{on "focusout" this.validate}}
            />
          </div>
          <button
            data-test-field="next"
            type="submit"
            class="btn btn--medium btn--brand
              {{if (or this.searchEmailTask.isRunning this.invalid) 'disabled' ''}}
              w-full"
            data-canary="login-next-button"
          >
            <span class="lock"></span>
            Next
          </button>
        </:else>
      </LiquidIf>
    </form>

    <div class="mt-4 text-center">
      <a
        data-test-forgot-password
        href="#"
        class="text-xs text-carbon-50"
        {{on "click" (preventDefault (fn @redirectToEnvoy "/users/password/new"))}}
      >
        Forgot your password?
      </a>
      {{#if @emailFound}}
        <a href="#" class="text-xs text-carbon-50 ml-4" {{on "click" (preventDefault (fn @changeEmail this.email))}}>
          Change email
        </a>
        {{#if @samlUrl}}
          <div class="w-full block mt-2">
            <a
              data-test-saml-login
              class="text-xs mt-4 text-carbon-50 w-full text-center"
              href={{@samlUrl}}
              {{on "click" (preventDefault @logInViaSAML)}}
            >
              Log in using Single Sign On
            </a>
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
